<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-x-4"
    enter-to-class="opacity-100 translate-x-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-x-0"
    leave-to-class="opacity-0 translate-x-4"
  >
    <div
      v-if="isOpen"
      class="audience-ai-panel shadow-md fixed top-6 bottom-6 w-[480px] bg-surface-light rounded-2xl flex flex-col overflow-hidden z-[60]"
      :style="panelStyle"
    >
      <!-- Header — raised above Intercom frame so tabs and close remain clickable -->
      <div class="audience-ai-panel__header relative z-[2147483003] bg-surface-light flex items-center gap-3 px-4 py-3 border-b border-neutral-200">
        <div class="flex-1">
          <AM3ToggleButton
            v-model="activeTab"
            :options="tabs"
            type="slider"
            stretch
            size="sm"
          />
        </div>
        <button
          class="flex-shrink-0 rounded-full size-9 flex items-center justify-center hover:bg-surfaces-neutral hover:text-brand transition-colors duration-75"
          aria-label="Close AI panel"
          @click="close"
        >
          <AM3Icon
            type="x-mark"
            size="md"
            color="strong"
          />
        </button>
      </div>

      <!-- Body -->
      <div
        id="ai-panel-body"
        class="flex-1 flex flex-col min-h-0 overflow-hidden bg-surface-light"
      >
        <div
          v-if="activeTab === 'marketing'"
          class="flex-1 flex flex-col min-h-0"
        >
          <iframe
            src="https://www.chatbase.co/chatbot-iframe/0NObsL5_SdKTFE6NPoutd"
            width="100%"
            frameborder="0"
            class="flex-1 min-h-0 w-full border-none"
          />
        </div>
        <!-- Intercom fills this space via fixed positioning matched to this element's rect -->
        <div
          v-if="activeTab === 'support'"
          class="flex-1"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3ToggleButton from '@/components/am3-generic/inputs/selection/AM3ToggleButton.vue'
import { useAudienceAIPanel } from '@/composables/useAudienceAIPanel'
import { useEscapeToClose } from '@/composables/useEscapeToClose'

const store = useStore()
const { isOpen, activeTab, close } = useAudienceAIPanel()

const isGlobalDrawerOpen = computed(() => store.getters['globalDrawerModule/isDrawerOpen'])

const panelStyle = computed(() => ({
  right: isGlobalDrawerOpen.value ? '504px' : '24px',
  transition: 'right 300ms ease-in-out',
}))

const tabs = [
  { value: 'marketing', text: 'Marketing expert', iconLeft: 'sparkles' },
  { value: 'support', text: 'Support', iconLeft: 'logo' },
]

const SUPPORT_OPEN_CLASS = 'audience-ai-support-open'

let onHideRegistered = false
let frameObserver: MutationObserver | null = null

useEscapeToClose(isOpen, close)

function applyIntercomFrameStyles() {
  const frame = document.querySelector('.intercom-messenger-frame') as HTMLElement | null
  const body = document.getElementById('ai-panel-body')
  if (!frame || !body) return

  const rect = body.getBoundingClientRect()
  frame.style.setProperty('position', 'fixed', 'important')
  frame.style.setProperty('left', `${rect.left}px`, 'important')
  frame.style.setProperty('top', `${rect.top}px`, 'important')
  frame.style.setProperty('width', `${rect.width}px`, 'important')
  frame.style.setProperty('height', `${rect.height}px`, 'important')
  frame.style.setProperty('right', 'auto', 'important')
  frame.style.setProperty('bottom', 'auto', 'important')
  frame.style.setProperty('max-height', 'none', 'important')
  frame.style.setProperty('border-radius', '0 0 1rem 1rem', 'important')
  frame.style.setProperty('overflow', 'hidden', 'important')
  frame.style.setProperty('transform', 'none', 'important')
  frame.style.setProperty('animation', 'none', 'important')
  frame.style.setProperty('transition', 'none', 'important')
  frame.style.setProperty('pointer-events', 'auto', 'important')
  frame.style.setProperty('visibility', 'visible', 'important')
}

function clearIntercomFrameStyles() {
  const frame = document.querySelector('.intercom-messenger-frame') as HTMLElement | null
  if (!frame) return
  ;['position', 'left', 'top', 'right', 'bottom', 'width', 'height', 'max-height',
    'border-radius', 'overflow', 'transform', 'animation', 'transition',
    'pointer-events', 'visibility'].forEach(prop => frame.style.removeProperty(prop))
}

function showIntercom() {
  document.body.classList.add(SUPPORT_OPEN_CLASS)

  const frame = document.querySelector('.intercom-messenger-frame') as HTMLElement | null
  if (frame && frame.getBoundingClientRect().height > 0) {
    applyIntercomFrameStyles()
    return
  }

  // Frame not ready — open Intercom and wait for it to render
  if (window.Intercom) {
    window.Intercom('update', { alignment: 'right', horizontal_padding: 0, vertical_padding: 0 })
    window.Intercom('show')
    if (!onHideRegistered) {
      onHideRegistered = true
      window.Intercom('onHide', () => {
        onHideRegistered = false
        hideIntercom()
        activeTab.value = 'marketing'
      })
    }
  }

  // Suppress any flash while the frame is loading
  if (frame) {
    frame.style.setProperty('visibility', 'hidden', 'important')
    frame.style.setProperty('animation', 'none', 'important')
    frame.style.setProperty('transition', 'none', 'important')
  }

  frameObserver?.disconnect()
  frameObserver = new MutationObserver(() => {
    const f = document.querySelector('.intercom-messenger-frame') as HTMLElement | null
    if (f) {
      f.style.setProperty('visibility', 'hidden', 'important')
      f.style.setProperty('animation', 'none', 'important')
      f.style.setProperty('transition', 'none', 'important')
      if (f.getBoundingClientRect().height > 0) {
        frameObserver?.disconnect()
        frameObserver = null
        applyIntercomFrameStyles()
      }
    }
  })
  frameObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] })
  // Safety: disconnect after 10s if Intercom never loads
  setTimeout(() => { frameObserver?.disconnect(); frameObserver = null }, 10000)
}

function hideIntercom() {
  document.body.classList.remove(SUPPORT_OPEN_CLASS)
  clearIntercomFrameStyles()
  frameObserver?.disconnect()
  frameObserver = null
  if (window.Intercom) {
    window.Intercom('update', { alignment: 'right', horizontal_padding: 20, vertical_padding: 20 })
    window.Intercom('hide')
  }
}

function onResize() {
  if (isOpen.value && activeTab.value === 'support') {
    applyIntercomFrameStyles()
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  hideIntercom()
  window.removeEventListener('resize', onResize)
})

watch(activeTab, (newVal) => {
  if (newVal === 'support' && isOpen.value) {
    nextTick(showIntercom)
  }
  else {
    hideIntercom()
  }
})

watch(isOpen, (open) => {
  if (!open) {
    hideIntercom()
  }
  else if (activeTab.value === 'support') {
    nextTick(showIntercom)
  }
})

watch(isGlobalDrawerOpen, () => {
  if (isOpen.value && activeTab.value === 'support') {
    // Wait for the right-transition to finish (300ms) before snapping the Intercom frame
    setTimeout(applyIntercomFrameStyles, 310)
  }
})
</script>

<style>
/* Body area becomes transparent so the Intercom frame shows through */
body.audience-ai-support-open #ai-panel-body {
  background-color: transparent !important;
  pointer-events: none !important;
}

/* Kill Intercom's shadow and animation while the panel is managing it */
body.audience-ai-support-open .intercom-messenger-frame {
  animation: none !important;
  transition: none !important;
  box-shadow: none !important;
}
</style>
